import { EKnowledgeProcessingStatus } from "@frontend-shared/types";
import { FileText, Trash } from "lucide-react";
import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { KnowledgeProcessingErrorText } from "@/pages/knowledge/components/KnowledgeProcessingErrorText";

import type { IFolderKnowledgeFileRowProps } from "./types";

// NOTE: One stored file in a chat folder's knowledge panel. NSAI-7817: a file
// that failed to index now shows the translated reason under its name.
const FolderKnowledgeFileRowInner: React.FC<IFolderKnowledgeFileRowProps> = ({
  file,
  onDelete,
}) => {
  const { t } = useTranslation("sidebar");
  const isFailed = file.processingStatus === EKnowledgeProcessingStatus.FAILED;

  const statusLabel = useMemo((): string | null => {
    if (isFailed) {
      return t("chat_folder_list.folder_settings.file_failed");
    }
    if (
      file.processingStatus === EKnowledgeProcessingStatus.PENDING ||
      file.processingStatus === EKnowledgeProcessingStatus.PROCESSING
    ) {
      return t("chat_folder_list.folder_settings.file_processing");
    }
    return null;
  }, [file.processingStatus, isFailed, t]);

  const handleDelete = useCallback(
    (): void => onDelete(file),
    [file, onDelete],
  );

  return (
    <li
      className="rounded-md border border-border px-3 py-2 text-sm"
      data-testid={`folder-knowledge-file-${file.uuid}`}
    >
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate">{file.name}</span>
        {statusLabel && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {statusLabel}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={handleDelete}
          data-testid={`folder-knowledge-file-delete-${file.uuid}`}
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>
      {isFailed && (
        <KnowledgeProcessingErrorText
          code={file.processingErrorCode}
          rawError={file.processingError}
          testId={`folder-knowledge-file-processing-error-${file.uuid}`}
        />
      )}
    </li>
  );
};

export const FolderKnowledgeFileRow = memo(FolderKnowledgeFileRowInner);
